import React, { useState, useEffect } from 'react';
import TenantLayout from '../components/TenantLayout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Users, Search, Plus, X } from 'lucide-react';

export default function PartyMaster() {
    const { clientSlug } = useParams();
    const { api } = useAuth();
    const { t } = useLanguage();
    
    const [parties, setParties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    
    const [formData, setFormData] = useState({
        name: '', role: 'customer', gstin: '', address: '', contact_email: '', contact_phone: ''
    });

    const fetchParties = async () => {
        try {
            const res = await api.get(`/api/tenant/${clientSlug}/parties`);
            setParties(res.data);
        } catch (err) {
            toast.error("Failed to load parties");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchParties();
    }, [clientSlug]);

    const handleGstFetch = async () => {
        if (!formData.gstin || formData.gstin.length < 15) {
            toast.error("Enter a valid 15-digit GSTIN");
            return;
        }
        try {
            toast.info("Fetching GSTIN details...");
            const res = await api.get(`/api/public/gstin/${formData.gstin}`);
            setFormData({
                ...formData,
                name: res.data.business_name,
                address: res.data.address
            });
            toast.success("GST details auto-filled");
        } catch (err) {
            toast.error("Could not fetch details for this GSTIN");
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await api.post(`/api/tenant/${clientSlug}/parties`, formData);
            toast.success(t("Party added successfully") || "Party added successfully");
            setShowAddModal(false);
            setFormData({ name: '', role: 'customer', gstin: '', address: '', contact_email: '', contact_phone: '' });
            fetchParties();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to add party");
        }
    };

    return (
        <TenantLayout title={t("Party Master")} tagline={t("Manage your Suppliers and Customers")}>
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <Users size={20} className="text-[#0A192F]" />
                        <h2 className="text-lg font-bold text-[#0F284E]">{t("Directory")}</h2>
                    </div>
                    <button 
                        onClick={() => setShowAddModal(true)}
                        className="bg-[#EAB308] hover:bg-[#CA8A04] text-[#0A192F] px-4 py-2 rounded-lg font-bold shadow-sm transition-colors flex items-center gap-2"
                    >
                        <Plus size={16} /> {t("Add Party")}
                    </button>
                </div>
                
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left whitespace-nowrap min-w-[600px]">
                        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase font-mono tracking-wider text-slate-600">
                            <tr>
                                <th className="px-6 py-4">{t("Party Name")}</th>
                                <th className="px-6 py-4">{t("Role")}</th>
                                <th className="px-6 py-4">{t("GSTIN")}</th>
                                <th className="px-6 py-4">{t("Contact")}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-500">{t("Loading directory...")}</td></tr>
                            ) : parties.length === 0 ? (
                                <tr><td colSpan="4" className="px-6 py-8 text-center text-slate-500">{t("No parties found. Add one to get started.")}</td></tr>
                            ) : parties.map((party) => (
                                <tr key={party._id} className="hover:bg-slate-50/50">
                                    <td className="px-6 py-4 font-bold text-[#0F284E]">{party.name}</td>
                                    <td className="px-6 py-4">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${
                                            party.role === 'customer' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                        }`}>
                                            {party.role.toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 font-mono text-slate-600">{party.gstin || '-'}</td>
                                    <td className="px-6 py-4 text-slate-500">
                                        {party.contact_email && <div>{party.contact_email}</div>}
                                        {party.contact_phone && <div>{party.contact_phone}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {showAddModal && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 border border-slate-200">
                        <h3 className="text-xl font-bold text-[#0F284E] mb-4 border-b border-slate-100 pb-4 flex justify-between items-center">
                            <span>{t("Add New Party")}</span>
                            <button onClick={() => setShowAddModal(false)} className="text-slate-400 hover:text-slate-700">
                                <X size={20} />
                            </button>
                        </h3>
                        
                        <div className="mb-6 bg-blue-50 border border-blue-100 p-4 rounded-lg">
                            <label className="block text-xs font-bold text-blue-800 uppercase tracking-wide mb-2">{t("Smart GST Auto-Fetch")}</label>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <input 
                                    type="text" 
                                    placeholder={t("Enter 15-digit GSTIN")}
                                    value={formData.gstin}
                                    onChange={(e) => setFormData({...formData, gstin: e.target.value})}
                                    className="w-full sm:flex-1 h-12 sm:h-10 px-3 rounded-md border border-blue-200 text-sm focus:ring-2 focus:ring-blue-500 uppercase font-mono" 
                                    maxLength="15"
                                />
                                <button 
                                    type="button"
                                    onClick={handleGstFetch}
                                    className="w-full sm:w-auto min-h-[44px] sm:min-h-0 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium text-sm transition-colors flex items-center justify-center gap-2"
                                >
                                    <Search size={16} /> {t("Fetch")}
                                </button>
                            </div>
                            <p className="text-xs text-blue-600 mt-2 italic">{t("Or enter details manually below if API fails")}</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">{t("Party Role")}</label>
                                    <select 
                                        value={formData.role} 
                                        onChange={e => setFormData({...formData, role: e.target.value})}
                                        className="w-full h-12 sm:h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]"
                                    >
                                        <option value="customer">{t("Customer (Buyer)")}</option>
                                        <option value="supplier">{t("Supplier (Vendor)")}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">{t("Business Name *")}</label>
                                    <input type="text" required value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full h-12 sm:h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">{t("Full Address")}</label>
                                <textarea value={formData.address} onChange={e => setFormData({...formData, address: e.target.value})} className="w-full p-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F] min-h-[80px]"></textarea>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">{t("Contact Email")}</label>
                                    <input type="email" value={formData.contact_email} onChange={e => setFormData({...formData, contact_email: e.target.value})} className="w-full h-12 sm:h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1">{t("Contact Phone")}</label>
                                    <input type="text" value={formData.contact_phone} onChange={e => setFormData({...formData, contact_phone: e.target.value})} className="w-full h-12 sm:h-10 px-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-[#0A192F]" />
                                </div>
                            </div>

                            <div className="pt-4 flex flex-col-reverse sm:flex-row justify-end gap-2 border-t border-slate-100">
                                <button type="button" onClick={() => setShowAddModal(false)} className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors">{t("Cancel")}</button>
                                <button type="submit" className="w-full sm:w-auto px-4 py-3 sm:py-2 rounded-lg bg-[#0A192F] hover:bg-[#0F284E] text-white font-medium shadow-sm transition-colors">{t("Save Party")}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </TenantLayout>
    );
}
